import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Grid,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Timeline as TimelineIcon
} from '@mui/icons-material';
import CommonChart, { ChartSeries } from './CommonChart';
import { useOktaAuth } from '@okta/okta-react';
import AxiosInstance from '../configs/axiosConfig';

// Test Result Interfaces
interface StareTestResult {
    uploadDate: string;
    blinkedAt: string;
}

interface FullPartialBlinkTestResult {
    uploadDate: string;
    averageTimeBetweenBlinks: number;
    partialBlinks: number;
    numberOfBlinks: number;
}

interface ReadingTestResult {
    uploadDate: string;
    averageWordsPerMinute: number;
}

interface EyeRednessTestResult {
    uploadDate: string;
    rightEyeRedLevel: number;
    leftEyeRedLevel: number;
}

type TestResult = StareTestResult | FullPartialBlinkTestResult | ReadingTestResult | EyeRednessTestResult;

interface TestData {
    testName: string;
    displayName: string;
    data: TestResult[];
    loading: boolean;
    error: string | null;
}

interface PatientTestResultsProps {
    userid: string | undefined;
}

const PatientTestResults: React.FC<PatientTestResultsProps> = ({ userid }) => {
    const [testData, setTestData] = useState<TestData[]>([]);
    const { authState } = useOktaAuth();

    // Test configurations
    const testConfigs = [
        { testName: "Stare-Test", displayName: "Stare Test" },
        { testName: "Eye-Redness-Test", displayName: "Eye Redness Test" },
        { testName: "Full-and-Partial-Blink-Test", displayName: "Full and Partial Blink Test" },
        { testName: "Reading-Test", displayName: "Reading Test" }
    ];

    // API call function
    const fetchTestData = async (testName: string): Promise<TestResult[]> => {
        try {
            console.log(`🔍 Fetching test data for: ${testName}`, {
                userid,
                url: `/patients/metadata?call=webapp&userid=${userid}&testName=${testName}`
            });
            const response = await AxiosInstance.get(`/patients/metadata?call=webapp&userid=${userid}&testName=${testName}`);
            console.log(`✅ Successfully fetched ${testName}:`, response.data);
            return response.data;
        } catch (error: any) {
            console.error(`❌ Error fetching ${testName}:`, {
                error,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                headers: error.config?.headers
            });

            // For development/testing, you can uncomment the following lines and import mockTestData
            // import { mockTestData } from './mockTestData';
            // return mockTestData[testName as keyof typeof mockTestData] || [];

            throw error;
        }
    };

    // Initialize test data on component mount
    useEffect(() => {
        // Don't fetch data if not authenticated
        if (!authState?.isAuthenticated) {
            console.log('⏸️ Waiting for authentication before fetching test data');
            return;
        }

        console.log('🚀 Auth state ready, initializing test data fetch');

        const initializeTestData = async () => {
            const initialTestData: TestData[] = testConfigs.map(config => ({
                testName: config.testName,
                displayName: config.displayName,
                data: [],
                loading: true,
                error: null
            }));

            setTestData(initialTestData);

            // Fetch data for each test
            for (const config of testConfigs) {
                try {
                    const data = await fetchTestData(config.testName);
                    setTestData(prevData =>
                        prevData.map(test =>
                            test.testName === config.testName
                                ? { ...test, data, loading: false }
                                : test
                        )
                    );
                } catch (error) {
                    setTestData(prevData =>
                        prevData.map(test =>
                            test.testName === config.testName
                                ? { ...test, loading: false, error: 'Failed to load test data' }
                                : test
                        )
                    );
                }
            }
        };

        initializeTestData();
    }, [userid, authState?.isAuthenticated]);

    // Helper functions to create chart data for each test type
    const createStareTestChart = (data: StareTestResult[]): { series: ChartSeries[], xAxisData: string[] } => {
        const sortedData = [...data].sort((a, b) => new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime());
        return {
            series: [{
                id: 'blinkedAt',
                label: 'Blinked At (seconds)',
                data: sortedData.map(item => parseFloat(item.blinkedAt)),
                color: '#1976d2'
            }],
            xAxisData: sortedData.map(item => new Date(item.uploadDate).toLocaleDateString())
        };
    };

    const createBlinkTestChart = (data: FullPartialBlinkTestResult[]): { series: ChartSeries[], xAxisData: string[] } => {
        const sortedData = [...data].sort((a, b) => new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime());
        return {
            series: [
                {
                    id: 'numberOfBlinks',
                    label: 'Number of Blinks',
                    data: sortedData.map(item => item.numberOfBlinks),
                    color: '#008AFF',
                    type: 'bar',
                    stack: 'total'
                },
                {
                    id: 'partialBlinks',
                    label: 'Partial Blinks',
                    data: sortedData.map(item => item.partialBlinks),
                    color: '#FF8400',
                    type: 'bar',
                    stack: 'total'
                },
                {
                    id: 'averageTimeBetweenBlinks',
                    label: 'Avg Time Between Blinks (s)',
                    data: sortedData.map(item => item.averageTimeBetweenBlinks),
                    color: '#FF0031',
                    type: 'line',
                    yAxisId: 'right-axis'
                }
            ],
            xAxisData: sortedData.map(item => new Date(item.uploadDate).toLocaleDateString())
        };
    };

    const createReadingTestChart = (data: ReadingTestResult[]): { series: ChartSeries[], xAxisData: string[] } => {
        const sortedData = [...data].sort((a, b) => new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime());
        return {
            series: [{
                id: 'wordsPerMinute',
                label: 'Words Per Minute',
                data: sortedData.map(item => item.averageWordsPerMinute),
                color: '#ff9800'
            }],
            xAxisData: sortedData.map(item => new Date(item.uploadDate).toLocaleDateString())
        };
    };

    const createEyeRednessChart = (data: EyeRednessTestResult[]): { series: ChartSeries[], xAxisData: string[] } => {
        const sortedData = [...data].sort((a, b) => new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime());
        
        return {
            series: [
                {
                    id: 'leftEye',
                    label: 'Left Eye Level',
                    data: sortedData.map(item => item.leftEyeRedLevel),
                    color: '#4caf50' // Green
                },
                {
                    id: 'rightEye',
                    label: 'Right Eye Level',
                    data: sortedData.map(item => item.rightEyeRedLevel),
                    color: '#ff9800' // Orange
                }
            ],
            xAxisData: sortedData.map(item => new Date(item.uploadDate).toLocaleDateString())
        };
    };

    // Function to get appropriate chart data based on test type
    const getChartData = (test: TestData) => {
        switch (test.testName) {
            case 'Stare-Test':
                return createStareTestChart(test.data as StareTestResult[]);
            case 'Full-and-Partial-Blink-Test':
                return createBlinkTestChart(test.data as FullPartialBlinkTestResult[]);
            case 'Reading-Test':
                return createReadingTestChart(test.data as ReadingTestResult[]);
            case 'Eye-Redness-Test':
                return createEyeRednessChart(test.data as EyeRednessTestResult[]);
            default:
                return { series: [], xAxisData: [] };
        }
    };

    // Function to get chart type and data based on test
    const getChartConfig = (test: TestData): { type: 'line' | 'bar', data: { series: ChartSeries[], xAxisData: string[] } } => {
        const chartData = getChartData(test);

        switch (test.testName) {
            case 'Stare-Test':
                return { type: 'line', data: chartData };
            case 'Reading-Test':
                return { type: 'line', data: chartData };
            case 'Full-and-Partial-Blink-Test':
                return { type: 'line', data: chartData }; // Mixed chart with bars and line
            case 'Eye-Redness-Test':
                return { type: 'bar', data: chartData };
            default:
                return { type: 'line', data: chartData };
        }
    };

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <TimelineIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                    Test Results & Analytics
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {testData.map((test) => (
                    <Grid item xs={6} key={test.testName}>
                        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <CardContent sx={{ p: 3 }}>
                                {/* Test Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, flex: 1 }}>
                                        {test.displayName}
                                    </Typography>
                                    {test.loading && (
                                        <CircularProgress size={20} />
                                    )}
                                    {!test.loading && test.data.length > 0 && (
                                        <Chip
                                            label={`${test.data.length} record${test.data.length !== 1 ? 's' : ''}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>

                                {/* Loading State */}
                                {test.loading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                        <CircularProgress />
                                    </Box>
                                )}

                                {/* Error State */}
                                {test.error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {test.error}
                                    </Alert>
                                )}

                                {/* No Data State */}
                                {!test.loading && !test.error && test.data.length === 0 && (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography color="text.secondary">
                                            No test data available for {test.displayName}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Data Available - Show Single Chart */}
                                {!test.loading && !test.error && test.data.length > 0 && (
                                    <Box>
                                        {(() => {
                                            const chartConfig = getChartConfig(test);
                                            return (
                                                <>
                                                    <CommonChart
                                                        type={chartConfig.type}
                                                        title={test.displayName}
                                                        series={chartConfig.data.series}
                                                        xAxisData={chartConfig.data.xAxisData}
                                                        height={300}
                                                        xAxisLabel="Date"
                                                        yAxisLabel="Count"
                                                        rightYAxisLabel={test.testName === 'Full-and-Partial-Blink-Test' ? 'Avg Time (s)' : undefined}
                                                        showGrid={true}
                                                        showLegend={test.testName === 'Eye-Redness-Test' || test.testName === 'Full-and-Partial-Blink-Test'}
                                                        showTooltip={true}
                                                        margin={test.testName === 'Full-and-Partial-Blink-Test' ? { top: 50, right: 80, bottom: 50, left: 60 } : undefined}
                                                    />
                                                </>
                                            );
                                        })()}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default PatientTestResults;